import { useRef, useState } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { Scissors, Play, X, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const sec = (t) => `${t.toFixed(1)}s`;

const Range = ({ value, max, onChange, color, testId, labels, mark }) => (
  <SliderPrimitive.Root data-testid={testId} className="relative flex w-full touch-none select-none items-center h-5" min={0} max={max} step={0.1}
    value={value} onValueChange={onChange} minStepsBetweenThumbs={5}>
    <SliderPrimitive.Track className="relative h-1.5 w-full grow rounded-full bg-[#2A3245]">
      <SliderPrimitive.Range className={`absolute h-full rounded-full ${color}`} />
    </SliderPrimitive.Track>
    {mark != null && mark < max && (
      <span data-testid={`${testId}-shot-join`} title={`Where the two shots meet (${mark.toFixed(1)}s)`} style={{ left: `${(mark / max) * 100}%` }}
        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rotate-45 bg-cyan-300 pointer-events-none" />
    )}
    {value.map((_, i) => (
      <SliderPrimitive.Thumb key={i} aria-label={labels[i]} data-testid={`${testId}-thumb-${i}`} className="block h-4 w-4 rounded-full border-2 border-white bg-[#11141E] shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400" />
    ))}
  </SliderPrimitive.Root>
);

export const TrimPanel = ({ s, aspect, onClose, reload, init }) => {
  const v = useRef(null);
  const previewing = useRef(false);
  const [dur, setDur] = useState(0);
  const [keep, setKeep] = useState([0, 0]);
  const [cutOn, setCutOn] = useState(false);
  const [cut, setCut] = useState([0, 0]);
  const [saving, setSaving] = useState(false);
  const fade = 0.25;
  const result = keep[1] - keep[0] - (cutOn ? cut[1] - cut[0] + fade : 0);
  const cutOk = !cutOn || (cut[0] >= keep[0] + 0.5 && cut[1] <= keep[1] - 0.5);
  const changed = keep[0] > 0.05 || keep[1] < dur - 0.05 || cutOn;

  const loaded = () => {
    const d = Math.floor(v.current.duration * 10) / 10;
    const a = Math.min(Math.ceil((init?.start ?? 0) * 10) / 10, d - 1), b = Math.max(Math.min(Math.floor((init?.end ?? d) * 10) / 10, d), a + 1);
    setDur(d); setKeep([+a.toFixed(1), +b.toFixed(1)]);
    if (init?.cut) { setCutOn(true); setCut([Math.floor(init.cut[0] * 10) / 10, Math.ceil(init.cut[1] * 10) / 10]); v.current.currentTime = Math.max(0, init.cut[0] - 2); }
    else setCut([+(d * 0.4).toFixed(1), +(d * 0.55).toFixed(1)]);
    if (init?.start) v.current.currentTime = a;
    else if (init?.end) v.current.currentTime = Math.max(0, b - 2);
  };
  const tick = () => {
    if (!previewing.current) return;
    const el = v.current, t = el.currentTime;
    if (t < keep[0] - 0.05) el.currentTime = keep[0];
    else if (cutOn && t >= cut[0] && t < cut[1]) el.currentTime = cut[1];
    else if (t >= keep[1]) { el.pause(); previewing.current = false; }
  };
  const preview = () => { previewing.current = true; v.current.currentTime = keep[0]; v.current.play(); };
  const here = () => +(v.current?.currentTime || 0).toFixed(1);
  const setKeepAt = (i) => setKeep((k) => (i === 0 ? [Math.min(here(), k[1] - 1), k[1]] : [k[0], Math.max(here(), k[0] + 1)]));
  const setCutAt = (i) => setCut((c) => (i === 0 ? [Math.min(here(), c[1] - 0.5), c[1]] : [c[0], Math.max(here(), c[0] + 0.5)]));

  const save = async () => {
    if (result < 1) return toast.error("Keep at least 1 second of the clip");
    if (!cutOk) return toast.error("The removed part must be inside the kept clip, with at least 0.5s on each side");
    setSaving(true);
    try {
      await http.post(`/scenes/${s.id}/trim`, { start: keep[0], end: keep[1], ...(cutOn ? { cut_from: cut[0], cut_to: cut[1] } : {}) });
      toast.success("Trimming… it will appear as a new take in a few seconds; the original stays in Takes");
      onClose(); await reload();
    } catch (e) { toast.error(errMsg(e)); setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="trim-panel">
      <div className={`relative bg-black rounded-lg overflow-hidden border border-amber-500/40 mx-auto ${aspect === "9:16" ? "aspect-[9/16] max-h-[52vh]" : "aspect-video w-full max-w-[calc(52vh*16/9)]"}`}>
        <video ref={v} data-testid="trim-video" src={fileUrl(s.video_path)} poster={fileUrl(s.thumb_path)} controls playsInline
          onLoadedMetadata={loaded} onTimeUpdate={tick} onPause={() => { previewing.current = false; }} className="w-full h-full object-contain" />
      </div>
      <div className="panel p-4 space-y-4">
        <div className="flex items-center gap-2 text-sm">
          <Scissors className="w-4 h-4 text-amber-400" /><span className="font-medium">Trim scene {s.number}</span>
          <span className="ml-auto font-mono text-xs text-slate-400" data-testid="trim-result-length">
            {sec(dur)} → <span className={result < 1 ? "text-red-400" : "text-amber-400"}>{sec(Math.max(0, result))}</span>
          </span>
        </div>
        <div className="space-y-1.5">
          <div className="flex justify-between font-mono text-[10px] text-slate-500"><span>KEEP FROM {sec(keep[0])}</span><span>TO {sec(keep[1])}</span></div>
          <Range testId="trim-keep-range" value={keep} max={dur || 1} onChange={setKeep} color="bg-amber-500" labels={["Keep from", "Keep to"]} mark={dur ? s.shot_join : null} />
          {s.shot_join && <div className="text-[10px] text-slate-500 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rotate-45 bg-cyan-300 inline-block" />Shots meet at {s.shot_join.toFixed(1)}s</div>}
          <div className="flex gap-2">
            <button data-testid="trim-start-here-btn" onClick={() => setKeepAt(0)} className="text-[11px] text-cyan-400 hover:underline">Start at playhead</button>
            <button data-testid="trim-end-here-btn" onClick={() => setKeepAt(1)} className="text-[11px] text-cyan-400 hover:underline ml-auto">End at playhead</button>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
          <Checkbox data-testid="trim-cut-middle-checkbox" checked={cutOn} onCheckedChange={(c) => setCutOn(!!c)} />Also remove a part in the middle
        </label>
        {cutOn && (
          <div className="space-y-1.5" data-testid="trim-cut-section">
            <div className="flex justify-between font-mono text-[10px] text-slate-500"><span>REMOVE FROM {sec(cut[0])}</span><span>TO {sec(cut[1])}</span></div>
            <Range testId="trim-cut-range" value={cut} max={dur || 1} onChange={setCut} color="bg-red-500" labels={["Remove from", "Remove to"]} mark={dur ? s.shot_join : null} />
            <div className="flex gap-2">
              <button data-testid="trim-cut-from-here-btn" onClick={() => setCutAt(0)} className="text-[11px] text-cyan-400 hover:underline">Remove from playhead</button>
              <button data-testid="trim-cut-to-here-btn" onClick={() => setCutAt(1)} className="text-[11px] text-cyan-400 hover:underline ml-auto">Remove to playhead</button>
            </div>
            {!cutOk && <div className="text-[11px] text-red-400" data-testid="trim-cut-error">Keep the removed part inside the kept clip, with 0.5s on each side.</div>}
            <div className="text-[11px] text-slate-500">The two pieces are joined with a quick {fade}s cross-fade. Cutting where the camera angle changes looks most natural; avoid cutting mid-sentence.</div>
          </div>
        )}
        {keep[1] < dur - 0.05 && (
          <div className="text-[11px] text-slate-500" data-testid="trim-end-note">The next scene will continue from the new last frame once you approve this take.</div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button data-testid="trim-preview-btn" size="sm" variant="secondary" disabled={!dur} onClick={preview} className="rounded-full"><Play className="w-3.5 h-3.5 mr-1.5" />Preview trim</Button>
          <Button data-testid="trim-cancel-btn" size="sm" variant="ghost" onClick={onClose} className="rounded-full"><X className="w-3.5 h-3.5 mr-1.5" />Cancel</Button>
          <Button data-testid="trim-save-btn" size="sm" disabled={!dur || !changed || saving || result < 1 || !cutOk} onClick={save} className="rounded-full ml-auto">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}Save as new take
          </Button>
        </div>
      </div>
    </div>
  );
};
